import { getSupabaseClient } from '../data/supabase';

type RealtimeEventInput = {
  source: string;
  table?: string | null;
  eventType?: string | null;
};

type MinuteBucket = { minuteEpochMs: number; count: number };

const MAX_BUCKETS = 180;
const DEFAULT_MONTHLY_LIMIT = 5_000_000;

const state = {
  startedAtMs: Date.now(),
  totalEvents: 0,
  eventsBySource: new Map<string, number>(),
  eventsByTable: new Map<string, number>(),
  eventsByType: new Map<string, number>(),
  minuteBuckets: [] as MinuteBucket[],
};

function bump(map: Map<string, number>, key: string | null | undefined): void {
  const safeKey = key && key.trim() ? key.trim() : 'unknown';
  map.set(safeKey, (map.get(safeKey) ?? 0) + 1);
}

function currentMinuteEpochMs(nowMs = Date.now()): number {
  return Math.floor(nowMs / 60_000) * 60_000;
}

function ensureCurrentMinuteBucket(nowMs = Date.now()): MinuteBucket {
  const minuteEpochMs = currentMinuteEpochMs(nowMs);
  const existing = state.minuteBuckets[state.minuteBuckets.length - 1];
  if (existing && existing.minuteEpochMs === minuteEpochMs) return existing;
  const next: MinuteBucket = { minuteEpochMs, count: 0 };
  state.minuteBuckets.push(next);
  if (state.minuteBuckets.length > MAX_BUCKETS) {
    state.minuteBuckets.splice(0, state.minuteBuckets.length - MAX_BUCKETS);
  }
  return next;
}

function toPairs(map: Map<string, number>): Array<{ key: string; count: number }> {
  return Array.from(map.entries())
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count);
}

function getActiveChannelCount(): number {
  try {
    const supabase = getSupabaseClient() as any;
    const channels = typeof supabase?.getChannels === 'function' ? supabase.getChannels() : [];
    return Array.isArray(channels) ? channels.length : 0;
  } catch {
    return 0;
  }
}

function sumBucketsSince(cutoffMs: number): number {
  let total = 0;
  for (const bucket of state.minuteBuckets) {
    if (bucket.minuteEpochMs >= cutoffMs) total += bucket.count;
  }
  return total;
}

function formatRatePerMinute(events: number, minutes: number): number {
  if (minutes <= 0) return 0;
  return events / minutes;
}

export function recordRealtimeEvent(input: RealtimeEventInput): void {
  state.totalEvents += 1;
  bump(state.eventsBySource, input.source);
  bump(state.eventsByTable, input.table ?? 'unknown');
  bump(state.eventsByType, input.eventType ?? 'unknown');
  const bucket = ensureCurrentMinuteBucket();
  bucket.count += 1;
}

export function resetRealtimeTelemetry(): void {
  state.startedAtMs = Date.now();
  state.totalEvents = 0;
  state.eventsBySource.clear();
  state.eventsByTable.clear();
  state.eventsByType.clear();
  state.minuteBuckets = [];
}

export function getRealtimeDiagnostics(limitOverride?: number): {
  startedAtMs: number;
  uptimeMinutes: number;
  totalEvents: number;
  activeChannels: number;
  eventsLast1m: number;
  eventsLast5m: number;
  eventsLast15m: number;
  avgPerMinute: number;
  peakPerMinute: number;
  projectedMonthlyPerUser: number;
  projectedMonthlyFor100Users: number;
  monthlyLimit: number;
  projectedUsagePctFor100Users: number;
  bySource: Array<{ key: string; count: number }>;
  byTable: Array<{ key: string; count: number }>;
  byType: Array<{ key: string; count: number }>;
} {
  const nowMs = Date.now();
  const uptimeMinutes = Math.max(1 / 60, (nowMs - state.startedAtMs) / 60_000);
  const eventsLast1m = sumBucketsSince(nowMs - 60_000);
  const eventsLast5m = sumBucketsSince(nowMs - 5 * 60_000);
  const eventsLast15m = sumBucketsSince(nowMs - 15 * 60_000);
  const avgPerMinute = formatRatePerMinute(state.totalEvents, uptimeMinutes);
  const peakPerMinute = state.minuteBuckets.reduce((max, bucket) => Math.max(max, bucket.count), 0);
  const projectedMonthlyPerUser = avgPerMinute * 60 * 24 * 30;
  const projectedMonthlyFor100Users = projectedMonthlyPerUser * 100;
  const monthlyLimit = Math.max(1, Math.floor(limitOverride ?? DEFAULT_MONTHLY_LIMIT));
  const projectedUsagePctFor100Users = (projectedMonthlyFor100Users / monthlyLimit) * 100;
  return {
    startedAtMs: state.startedAtMs,
    uptimeMinutes,
    totalEvents: state.totalEvents,
    activeChannels: getActiveChannelCount(),
    eventsLast1m,
    eventsLast5m,
    eventsLast15m,
    avgPerMinute,
    peakPerMinute,
    projectedMonthlyPerUser,
    projectedMonthlyFor100Users,
    monthlyLimit,
    projectedUsagePctFor100Users,
    bySource: toPairs(state.eventsBySource),
    byTable: toPairs(state.eventsByTable),
    byType: toPairs(state.eventsByType),
  };
}
